import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ToggleSwitch, CollapseTransition } from '../../sdk';
import { Search, IdCard, Bot, Palette, BarChart3, Flame, Sun, BookOpen } from 'lucide-react';
import {
  NORMIES_MODES,
  NORMIES_MODE_MAP,
  DEFAULT_NORMIES_TOKEN_ID,
  normalizeNormiesConfig,
  POLAROID_COLORS,
  DEFAULT_POLAROID_COLOR,
} from '../../constants/normies';
import { PALETTE_PRESETS } from '../../constants/palette';
import { GENERATORS, getPalettesByFamily, preparePalette, PALETTE_MAP } from '../../constants/genArt';
import { renderThumbnail, renderGenerator } from '../../utils/genArtRenderers';
import { fetchNormieData, fetchMarketStats, fetchHistoryStats, fetchBurnHistory, fetchBurnedToken, fetchTopSale } from '../../utils/normiesApi';
import {
  createNormieCardPatch, createAgentCardPatch, createCanvasStoryPatch,
  createMarketDashboardPatch, createBurnStoryPatch,
  createGmPatch, createExplainerPatch, EXPLAINER_TOPICS,
} from '../../utils/normiesToSlide';
import { scrollEditorToSection } from '../../utils/scrollEditor';

// ── Template options for card modes (normie-card / agent-card) ──
const NORMIES_TEMPLATES = [
  { id: 'normies-full-bg', label: 'Full BG' },
  { id: 'normies-polaroid', label: 'Polaroid' },
];

// ── Curated generators for Normies backgrounds ──
const NORMIES_GENERATORS = ['meridian', 'halftonePanel', 'blockMaze', 'truchetCurves', 'sunburst'];
// Canvas Story: custom selection
const CANVAS_STORY_GENERATORS = ['meridian', 'blockMaze', 'circlePack', 'stipple'];
// Burn Story: sunburst only
const BURN_STORY_GENERATORS = ['sunburst', 'domainWarp'];
// GM: no halftone
const GM_GENERATORS = ['meridian', 'blockMaze', 'truchetCurves', 'sunburst'];
// Explainer: Composition default, all geo generators available
const EXPLAINER_GENERATORS = ['halftonePanel', 'blockMaze', 'truchetCurves', 'geoComposition', 'concentricFrames', 'sunburst'];

// ── 5 curated text palettes (including the Normies default) ──
const NORMIES_TEXT_PALETTE_IDS = ['normies', 'forest', 'mint', 'coral', 'lavender'];
const NORMIES_TEXT_PALETTES = PALETTE_PRESETS.filter(p => NORMIES_TEXT_PALETTE_IDS.includes(p.id));
// GM: all vivid + highlights for bold text on gen art backgrounds
const GM_TEXT_PALETTE_IDS = ['forest', 'neon', 'sunrise', 'volcano', 'bubblegum', 'aurora', 'prism', 'lagoon', 'solar', 'citrus', 'mint', 'sky', 'coral', 'lavender', 'gold'];
const GM_TEXT_PALETTES = PALETTE_PRESETS.filter(p => GM_TEXT_PALETTE_IDS.includes(p.id));

// Default text palette per template
const DEFAULT_TEXT_PALETTE = {
  'normies-full-bg': 'forest',
  'normies-polaroid': 'normies',
  'normies-dashboard': 'forest',
};

// ── Loading progress steps ──
const LOAD_STEPS = [
  { key: 'traits', label: 'READING TRAITS' },
  { key: 'canvas', label: 'SCANNING CANVAS' },
  { key: 'agent', label: 'CHECKING AGENT' },
  { key: 'done', label: 'READY' },
];

// ── Family accent for generator LED ──
const NORMIES_GEN_ACCENT = '#48494b';

// ── Helpers ──
function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : null;
}

function isColorDark(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return false;
  const lum = (parseInt(m[1], 16) * 299 + parseInt(m[2], 16) * 587 + parseInt(m[3], 16) * 114) / 1000;
  return lum < 128;
}

/**
 * Render a generator at production-like resolution (540×675) then downscale
 * to thumbnail size. This ensures patterns look exactly like the main preview
 * instead of having oversized features at small canvas sizes.
 */
const RENDER_W = 540, RENDER_H = 675;  // 0.5× production (1080×1350)
const THUMB_W = 192, THUMB_H = 240;

function renderScaledThumb(genId, paletteId, seed) {
  try {
    // Render at high resolution
    const big = document.createElement('canvas');
    big.width = RENDER_W;
    big.height = RENDER_H;
    const bigCtx = big.getContext('2d');
    renderGenerator(bigCtx, RENDER_W, RENDER_H, {
      type: genId, paletteId, seed,
      hueShift: 0, saturation: 100, brightness: 100,
    });

    // Downscale to thumbnail
    const small = document.createElement('canvas');
    small.width = THUMB_W;
    small.height = THUMB_H;
    const smallCtx = small.getContext('2d');
    smallCtx.drawImage(big, 0, 0, THUMB_W, THUMB_H);

    return small.toDataURL('image/jpeg', 0.85);
  } catch {
    // Fallback to direct small render
    return renderThumbnail(genId, paletteId, seed, THUMB_W, THUMB_H);
  }
}

/** Gear icon SVG — inline so no extra dependency */
function GearIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="2.5" />
      <path d="M6.7 1.6h2.6l.4 1.7a5 5 0 0 1 1.3.8l1.6-.5 1.3 2.3-1.2 1.2a5 5 0 0 1 0 1.5l1.2 1.2-1.3 2.3-1.6-.5a5 5 0 0 1-1.3.8l-.4 1.7H6.7l-.4-1.7a5 5 0 0 1-1.3-.8l-1.6.5-1.3-2.3 1.2-1.2a5 5 0 0 1 0-1.5L2.1 5.9l1.3-2.3 1.6.5a5 5 0 0 1 1.3-.8l.4-1.7z" />
    </svg>
  );
}

/** Section header with label + optional "More" button */
function SectionHeader({ label, onMore }) {
  return (
    <div className="normies-section-label">
      <span className="normies-section-label-text">{label}</span>
      {onMore && (
        <button className="normies-more-btn" onClick={onMore} type="button" title="More options in Gen Art editor">
          <GearIcon className="normies-more-btn-icon" />
          <span className="normies-more-btn-label">More</span>
        </button>
      )}
    </div>
  );
}

/** Pick display colors for a text palette swatch (4 cells) */
function textPaletteSwatchColors(preset) {
  const c = preset.colors;
  return [c.textPrimary || c.accent, c.accent, c.accentSoft || c.accent, c.textPrimary || '#fff'];
}

/**
 * NormiesEditor — self-contained Normies Studio control center.
 *
 * Uses existing QooQoo component styles:
 * - genart-family-btn for mode/template
 * - genart-generator-btn for background generators (4-col normies grid)
 * - genart-palette-btn for both art palette AND text palette (uniform sizing)
 */
// ── Comprehensive snapshot: every field ANY Normies patch can overwrite ──
// Used to restore the slide when Normies Studio is toggled off.
function capturePreNormiesSnapshot(slide) {
  return {
    // Text content
    title: slide.title,
    subtitle: slide.subtitle,
    subtitleEnabled: slide.subtitleEnabled,
    body: slide.body,
    bodyEnabled: slide.bodyEnabled,
    kicker: slide.kicker,
    kickerEnabled: slide.kickerEnabled,
    stats: slide.stats ? JSON.parse(JSON.stringify(slide.stats)) : slide.stats,
    meta: slide.meta ? JSON.parse(JSON.stringify(slide.meta)) : slide.meta,
    tagsEnabled: slide.tagsEnabled,
    // Footer
    footerEnabled: slide.footerEnabled,
    cta: slide.cta,
    ctaButton: slide.ctaButton,
    // Font
    fontPreset: slide.fontPreset,
    titleFontSize: slide.titleFontSize,
    kickerFontSize: slide.kickerFontSize,
    subtitleFontSize: slide.subtitleFontSize,
    bodyFontSize: slide.bodyFontSize,
    tagsFontSize: slide.tagsFontSize,
    statsFontSize: slide.statsFontSize,
    // Layout / palette
    template: slide.template,
    palette: slide.palette,
    // Background
    backgroundUrl: slide.backgroundUrl,
    bgAssetId: slide.bgAssetId,
    bgSolid: slide.bgSolid,
    gradient: slide.gradient ? JSON.parse(JSON.stringify(slide.gradient)) : slide.gradient,
    generative: slide.generative ? JSON.parse(JSON.stringify(slide.generative)) : slide.generative,
    // Widgets that some patches clear
    bulletsEnabled: slide.bulletsEnabled,
    progress: slide.progress ? JSON.parse(JSON.stringify(slide.progress)) : slide.progress,
  };
}

export function NormiesEditor({ normies, onChange, slide }) {
  const config = useMemo(() => normalizeNormiesConfig(normies), [normies]);
  const enabled = config.enabled;

  // ── Pre-Normies snapshot storage (keyed by slide.id) ──
  // Survives applyToSlide cycles because it's a ref, not part of the
  // normies config that gets replaced by full-slide patches.
  const preNormiesSnapshots = useRef({});

  const [tokenInput, setTokenInput] = useState(config.tokenId || DEFAULT_NORMIES_TOKEN_ID);
  const [loading, setLoading] = useState(false);
  const [loadStep, setLoadStep] = useState(-1);
  const [error, setError] = useState(null);
  const [loadedData, setLoadedData] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(
    config.enabled && slide?.template?.startsWith('normies-')
      ? slide.template
      : 'normies-polaroid'
  );
  const [selectedBgPalette, setSelectedBgPalette] = useState(
    config.enabled && slide?.generative?.paletteId
      ? slide.generative.paletteId
      : 'normies-mono'
  );
  const [selectedGenerator, setSelectedGenerator] = useState(
    config.enabled && slide?.generative?.type
      ? slide.generative.type
      : 'halftonePanel'
  );
  const [selectedMode, setSelectedMode] = useState(config.mode || 'normie-card');
  const [selectedTextPalette, setSelectedTextPalette] = useState(
    slide?.palette || DEFAULT_TEXT_PALETTE['normies-polaroid']
  );
  const [selectedPolaroidColor, setSelectedPolaroidColor] = useState(
    config.polaroidColor || DEFAULT_POLAROID_COLOR
  );

  // Refs for latest values
  const templateRef = useRef(selectedTemplate);
  const bgPaletteRef = useRef(selectedBgPalette);
  const generatorRef = useRef(selectedGenerator);
  const modeRef = useRef(selectedMode);
  const dataRef = useRef(loadedData);
  const textPaletteRef = useRef(selectedTextPalette);
  const polaroidColorRef = useRef(selectedPolaroidColor);
  const marketDataRef = useRef(null);
  const burnDataRef = useRef(null);
  const explainerTopicRef = useRef('what-is-normie');
  const loadingRef = useRef(false);
  templateRef.current = selectedTemplate;
  bgPaletteRef.current = selectedBgPalette;
  generatorRef.current = selectedGenerator;
  modeRef.current = selectedMode;
  dataRef.current = loadedData;
  textPaletteRef.current = selectedTextPalette;
  polaroidColorRef.current = selectedPolaroidColor;

  // ── Per-mode state isolation ──
  // Each isolated mode persists its own bgPalette, generator, and textPalette.
  // Switching modes saves outgoing state and restores incoming state (or defaults).
  // Normie Card and Agent Card are excluded — they share the global palette.
  const ISOLATED_MODES = useMemo(() => ['normie-card', 'agent-card', 'canvas-story', 'market-dashboard', 'burn-story', 'gm', 'explainer'], []);
  const modeStateRef = useRef({});

  // Seed initial mode state from the slide if we're already in an isolated mode
  const didSeedInitialModeState = useRef(false);
  if (!didSeedInitialModeState.current && enabled && ISOLATED_MODES.includes(selectedMode)) {
    modeStateRef.current[selectedMode] = {
      bgPalette: selectedBgPalette,
      generator: selectedGenerator,
      textPalette: selectedTextPalette,
    };
    didSeedInitialModeState.current = true;
  }

  /** Save current mode's visual state for later restoration */
  const saveModeState = useCallback((modeId) => {
    if (!ISOLATED_MODES.includes(modeId)) return;
    modeStateRef.current[modeId] = {
      bgPalette: bgPaletteRef.current,
      generator: generatorRef.current,
      textPalette: textPaletteRef.current,
      template: templateRef.current,
    };
  }, [ISOLATED_MODES]);

  /** Restore a mode's saved state, or return null if no saved state (use defaults) */
  const getSavedModeState = useCallback((modeId) => {
    return modeStateRef.current[modeId] || null;
  }, []);

  const autoLoadDone = useRef(false);
  const prevEnabled = useRef(enabled);
  const didAutoScroll = useRef(false);
  // Tracks the palette that was auto-set by a frame color change.
  // If the user manually picks a palette afterward, this goes stale —
  // so we only override when current palette still matches this value.
  const autoSetPaletteRef = useRef(null);
  // Suppresses the external palette sync effect during internal mode switches.
  // Set to true in handleModeApply, cleared after a short delay.
  const suppressExternalSyncRef = useRef(false);

  // ── Generator thumbnails (render at 540×675 then downscale for crisp small previews) ──
  const activeGenerators = selectedMode === 'canvas-story' ? CANVAS_STORY_GENERATORS : selectedMode === 'burn-story' ? BURN_STORY_GENERATORS : selectedMode === 'gm' ? GM_GENERATORS : selectedMode === 'explainer' ? EXPLAINER_GENERATORS : NORMIES_GENERATORS;
  const generatorThumbs = useMemo(() => {
    const genList = selectedMode === 'canvas-story' ? CANVAS_STORY_GENERATORS : selectedMode === 'burn-story' ? BURN_STORY_GENERATORS : selectedMode === 'gm' ? GM_GENERATORS : selectedMode === 'explainer' ? EXPLAINER_GENERATORS : NORMIES_GENERATORS;
    const thumbs = {};
    for (const genId of genList) {
      const gen = GENERATORS.find(g => g.id === genId);
      if (!gen) continue;
      try {
        thumbs[genId] = renderScaledThumb(genId, selectedBgPalette, 42);
      } catch {
        thumbs[genId] = null;
      }
    }
    return thumbs;
  }, [selectedBgPalette, selectedMode]);

  // ── Auto-scroll to Normies section when enabled ──
  useEffect(() => {
    if (enabled && !didAutoScroll.current) {
      didAutoScroll.current = true;
      // Small delay to let the UI expand, then scroll
      const timer = setTimeout(() => {
        scrollEditorToSection('normies');
      }, 150);
      return () => clearTimeout(timer);
    }
    if (!enabled) {
      didAutoScroll.current = false;
    }
  }, [enabled]);

  // ── Sync external palette changes ──
  // If the user changes palette in the main Gen Art editor, update our selection.
  // Suppressed during internal mode switches to prevent Card A's palette bleeding into Card B.
  useEffect(() => {
    if (suppressExternalSyncRef.current) return;
    const externalPalette = slide?.generative?.paletteId;
    if (enabled && externalPalette && externalPalette !== bgPaletteRef.current) {
      setSelectedBgPalette(externalPalette);
      bgPaletteRef.current = externalPalette;
      // Also persist this external change for the current mode
      saveModeState(modeRef.current);
    }
  }, [enabled, slide?.generative?.paletteId, saveModeState]);


  // ── Auto-load on first enable ──
  useEffect(() => {
    if (enabled && !prevEnabled.current) {
      autoLoadDone.current = false;
    }
    prevEnabled.current = enabled;
    if (enabled && !autoLoadDone.current && !loadedData && !loading) {
      autoLoadDone.current = true;
      doLoad(DEFAULT_NORMIES_TOKEN_ID);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const handleToggle = useCallback((on) => {
    const slideId = slide?.id;
    if (on) {
      // ── Snapshot the FULL slide state BEFORE Normies overwrites anything ──
      // Stored in a ref keyed by slide.id so it survives the applyToSlide
      // cycle (which replaces the normies config and loses _snapshot).
      const snapshot = capturePreNormiesSnapshot(slide);
      if (slideId) preNormiesSnapshots.current[slideId] = snapshot;
      autoLoadDone.current = false;
      onChange({ ...config, enabled: true, _snapshot: snapshot }, false);
    } else {
      // ── Restore pre-Normies state ──
      // Primary: ref-based snapshot (survives applyToSlide).
      // Fallback: config._snapshot (if ref was lost, e.g. component remount).
      const snapshot = (slideId && preNormiesSnapshots.current[slideId])
        || config._snapshot;
      autoLoadDone.current = false;
      if (snapshot) {
        // Full slide patch: restore ALL snapshotted fields + disable normies
        onChange({
          ...snapshot,
          normies: { ...config, enabled: false, _snapshot: undefined },
        }, true); // isFullSlidePatch — merges at slide level
        // Clean up the ref entry
        if (slideId) delete preNormiesSnapshots.current[slideId];
      } else {
        onChange({ ...config, enabled: false }, false);
      }
    }
  }, [config, slide, onChange]);

  // ── Scroll-safe apply ──
  // changedField tracks WHAT triggered the apply, so we only override
  // fields that are relevant to that change. This prevents Normies Studio
  // from resetting user choices made outside (e.g. in the main GenArt editor).
  //   null        → full override (data load, first apply)
  //   'mode'      → full override (new mode = fresh layout)
  //   'bgPalette' / 'generator' → only gen art fields
  //   'textPalette' / 'template' / 'polaroidColor' → only text palette
  const applyToSlide = useCallback((data, mode, template, bgPalette, generator, textPalette, polaroidColor, changedField = null) => {
    if (!data) return;

    let patch;
    if (mode === 'agent-card') {
      patch = createAgentCardPatch(data, { template });
      if (!patch) patch = createNormieCardPatch(data, { template });
    } else if (mode === 'canvas-story') {
      patch = createCanvasStoryPatch(data, { template });
    } else if (mode === 'market-dashboard') {
      patch = createMarketDashboardPatch(data, marketDataRef.current || {}, { template });
    } else if (mode === 'burn-story') {
      patch = createBurnStoryPatch(data, burnDataRef.current || {}, { template });
    } else if (mode === 'gm') {
      patch = createGmPatch(data, { template });
    } else if (mode === 'explainer') {
      patch = createExplainerPatch(data, { template, topicId: explainerTopicRef.current });
    } else {
      patch = createNormieCardPatch(data, { template });
    }

    // Gen art: always apply the Normies-selected generator + palette.
    // Inside Normies Studio, these selections are authoritative — stripping
    // them causes shallow-merge to overwrite the slide's generative object
    // and lose the type/paletteId (resulting in fallback to default generator).
    const isFullOverride = !changedField || changedField === 'mode';
    const isGenArtChange = changedField === 'bgPalette' || changedField === 'generator';

    if (isFullOverride || isGenArtChange) {
      // Apply gen art fields — override palette + generator from Normies refs
      if (patch.generative) {
        patch.generative.paletteId = bgPalette;
        patch.generative.type = generator;
      }
    } else {
      // Non-gen-art change (textPalette, template, polaroidColor, topic) —
      // strip generative entirely so we don't reset the user's background
      delete patch.generative;
    }

    // Text palette: only override on full-override or palette-related changes
    const isPaletteChange = changedField === 'textPalette' || changedField === 'template' || changedField === 'polaroidColor';
    if (isFullOverride || isPaletteChange) {
      if (textPalette) patch.palette = textPalette;
    }

    // Set polaroid color in normies config
    if (patch.normies) {
      patch.normies.polaroidColor = polaroidColor || DEFAULT_POLAROID_COLOR;
      // Preserve pre-Normies snapshot through full-slide patches.
      // The create*Patch() functions build a fresh normies object that doesn't
      // include _snapshot — without this, the snapshot set in handleToggle
      // is lost when applyToSlide fires (e.g. during auto-load).
      if (config._snapshot && !patch.normies._snapshot) {
        patch.normies._snapshot = config._snapshot;
      }
    }

    if (typeof onChange === 'function') {
      const scrollEl = document.getElementById('editor-scroll-container');
      const normiesEl = document.getElementById('section-normies');
      if (scrollEl && normiesEl) {
        const rectBefore = normiesEl.getBoundingClientRect();
        const scrollBefore = scrollEl.scrollTop;
        onChange(patch, true);
        const observer = new ResizeObserver(() => {
          const rectAfter = normiesEl.getBoundingClientRect();
          const drift = rectAfter.top - rectBefore.top;
          if (Math.abs(drift) > 2) {
            scrollEl.scrollTop = scrollBefore + drift;
          }
          observer.disconnect();
        });
        observer.observe(scrollEl);
        setTimeout(() => observer.disconnect(), 500);
      } else {
        onChange(patch, true);
      }
    }
  }, [onChange, config]);

  // Ref so the stale doLoad closure always calls the latest applyToSlide
  // (same pattern as templateRef / bgPaletteRef / generatorRef).
  const applyRef = useRef(applyToSlide);
  applyRef.current = applyToSlide;

  // ── Load from API ──
  const doLoad = useCallback(async (tid) => {
    const mode = modeRef.current;

    // ── Market mode: auto-resolve top sale token ID ──
    if (mode === 'market-dashboard') {
      setLoading(true);
      loadingRef.current = true;
      setError(null);
      setLoadStep(0);

      try {
        // Fetch top sale + market data in parallel
        const [topSaleRes, marketRes, historyStats] = await Promise.all([
          fetchTopSale(),
          fetchMarketStats(),
          fetchHistoryStats(),
        ]);

        marketDataRef.current = {
          stats: marketRes?.stats || {},
          historyStats: historyStats || {},
        };

        // Resolve token ID from top sale, fall back to user input
        const topTokenId = topSaleRes?.topSale?.tokenId;
        const resolvedTid = topTokenId || String(tid).trim();

        if (!resolvedTid || isNaN(resolvedTid)) {
          setError('No sales today and no token ID entered');
          setLoading(false);
          loadingRef.current = false;
          setLoadStep(-1);
          return;
        }

        setLoadStep(1);

        const data = await fetchNormieData(resolvedTid);
        if (!data) {
          setError(`Normie #${resolvedTid} not found`);
          setLoadedData(null);
          setLoading(false);
          loadingRef.current = false;
          setLoadStep(-1);
          return;
        }

        // Attach top sale metadata so the patch can use it
        if (topSaleRes?.topSale) {
          marketDataRef.current.topSale = topSaleRes.topSale;
          marketDataRef.current.salesCount = topSaleRes.salesCount;
        }

        await new Promise(r => setTimeout(r, 200));
        setLoadStep(2);
        await new Promise(r => setTimeout(r, 200));
        setLoadStep(3);
        setLoadedData(data);
        setTokenInput(resolvedTid);

        applyRef.current(data, mode, templateRef.current, bgPaletteRef.current, generatorRef.current, textPaletteRef.current, polaroidColorRef.current, null);
      } catch (err) {
        setError(`Failed: ${err.message}`);
        setLoadedData(null);
      } finally {
        setLoading(false);
        loadingRef.current = false;
        setTimeout(() => setLoadStep(-1), 1200);
      }
      return;
    }

    // ── Burn Story: auto-detect scenario ──
    if (mode === 'burn-story') {
      setLoading(true);
      loadingRef.current = true;
      setError(null);
      setLoadStep(0);

      try {
        // Fetch Normie data + burn checks in parallel
        const [normieData, burnedInfo, burnHistory] = await Promise.all([
          fetchNormieData(tid),
          fetchBurnedToken(tid).catch(() => null),
          fetchBurnHistory(tid).catch(() => null),
        ]);

        if (!normieData) {
          setError(`Normie #${tid} not found`);
          setLoadedData(null);
          setLoading(false);
          loadingRef.current = false;
          setLoadStep(-1);
          return;
        }

        setLoadStep(1);

        // Determine scenario
        const wasBurned = !!(burnedInfo && typeof burnedInfo === 'object' && Object.keys(burnedInfo).length > 0 && !burnedInfo.error);
        const hasReceiverBurns = !!(burnHistory && (
          (Array.isArray(burnHistory) && burnHistory.length > 0) ||
          (burnHistory.burns && burnHistory.burns.length > 0) ||
          (burnHistory.commits && burnHistory.commits.length > 0)
        ));

        if (wasBurned) {
          // ── Scenario 1: This Normie was burned ──
          burnDataRef.current = { burnHistory, burnedInfo, scenario: 'burned' };
          await new Promise(r => setTimeout(r, 200));
          setLoadStep(2);
          await new Promise(r => setTimeout(r, 200));
          setLoadStep(3);
          setLoadedData(normieData);
          setTokenInput(tid);
          applyRef.current(normieData, mode, templateRef.current, bgPaletteRef.current, generatorRef.current, textPaletteRef.current, polaroidColorRef.current, null);

        } else if (hasReceiverBurns) {
          // ── Scenario 2: Not burned, but powered by burns ──
          burnDataRef.current = { burnHistory, burnedInfo: null, scenario: 'receiver' };
          await new Promise(r => setTimeout(r, 200));
          setLoadStep(2);
          await new Promise(r => setTimeout(r, 200));
          setLoadStep(3);
          setLoadedData(normieData);
          setTokenInput(tid);
          applyRef.current(normieData, mode, templateRef.current, bgPaletteRef.current, generatorRef.current, textPaletteRef.current, polaroidColorRef.current, null);

        } else {
          // ── Scenario 3: No burn history → fallback to Normie Card ──
          setError(`No burn history found for Normie #${tid}. This Normie was not burned and does not appear to have received burn-powered canvas actions. Opening a regular Normie Card instead.`);
          await new Promise(r => setTimeout(r, 800));

          // Switch to normie-card mode
          const fallbackMode = 'agent-card';
          setSelectedMode(fallbackMode);
          modeRef.current = fallbackMode;
          burnDataRef.current = {};

          setLoadStep(2);
          await new Promise(r => setTimeout(r, 200));
          setLoadStep(3);
          setLoadedData(normieData);
          setTokenInput(tid);
          applyRef.current(normieData, fallbackMode, templateRef.current, bgPaletteRef.current, generatorRef.current, textPaletteRef.current, polaroidColorRef.current, null);
          // Clear the error after a moment since we successfully loaded
          setTimeout(() => setError(null), 4000);
        }
      } catch (err) {
        setError(`Failed: ${err.message}`);
        setLoadedData(null);
      } finally {
        setLoading(false);
        loadingRef.current = false;
        setTimeout(() => setLoadStep(-1), 1200);
      }
      return;
    }

    // ── All other modes: use manually entered token ID ──
    tid = String(tid).trim();
    if (!tid || isNaN(tid)) { setError('Enter a valid token ID (0–9999)'); return; }
    const numId = parseInt(tid, 10);
    if (numId < 0 || numId > 9999) { setError('Normie ID must be 0–9999'); return; }

    setLoading(true);
    loadingRef.current = true;
    setError(null);
    setLoadStep(0);

    try {
      const data = await fetchNormieData(tid);
      if (!data) {
        setError(`Normie #${tid} not found`);
        setLoadedData(null);
        setLoading(false);
        loadingRef.current = false;
        setLoadStep(-1);
        return;
      }

      setLoadStep(1);

      // Fetch mode-specific extra data in parallel
      const mode = modeRef.current;
      try {
        if (mode === 'market-dashboard' && !marketDataRef.current) {
          const [marketRes, historyStats] = await Promise.all([
            fetchMarketStats(),
            fetchHistoryStats(),
          ]);
          marketDataRef.current = {
            stats: marketRes?.stats || {},
            historyStats: historyStats || {},
          };
        } else if (mode === 'burn-story' && !burnDataRef.current) {
          const tid = data.tokenId;
          const [burnHistory, burnedInfo] = await Promise.all([
            fetchBurnHistory(tid),
            fetchBurnedToken(tid),
          ]);
          burnDataRef.current = { burnHistory, burnedInfo };
        }
      } catch (modeErr) {
        console.warn('[NormiesEditor] Mode-specific data fetch failed:', modeErr.message);
        // Continue — applyToSlide will use empty fallback data
      }

      await new Promise(r => setTimeout(r, 200));
      setLoadStep(2);
      await new Promise(r => setTimeout(r, 200));
      setLoadStep(3);
      setLoadedData(data);
      setTokenInput(tid);

      applyRef.current(data, modeRef.current, templateRef.current, bgPaletteRef.current, generatorRef.current, textPaletteRef.current, polaroidColorRef.current, null);
    } catch (err) {
      setError(`Failed: ${err.message}`);
      setLoadedData(null);
    } finally {
      setLoading(false);
      loadingRef.current = false;
      setTimeout(() => setLoadStep(-1), 1200);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoad = useCallback(() => { doLoad(tokenInput); }, [tokenInput, doLoad]);

  // ── Change handlers ──
  // Mode/template switches keep the current palettes — only explicit palette
  // clicks should change them. This prevents the jarring jumps the user noticed.
  // Modes where template is locked to the mode's default (no user choice)
  const TEMPLATE_LOCKED_MODES = ['canvas-story', 'market-dashboard', 'burn-story', 'gm', 'explainer'];

  const handleModeApply = useCallback(async (modeId) => {
    // ── Suppress external palette sync during mode switch ──
    // Prevents Card A's palette from bleeding into Card B via the sync effect.
    suppressExternalSyncRef.current = true;

    // ── Save outgoing mode's state before switching ──
    const outgoingMode = modeRef.current;
    saveModeState(outgoingMode);

    setSelectedMode(modeId); modeRef.current = modeId;

    // Auto-set the locked template for modes that have a dedicated one
    const modeConf = NORMIES_MODE_MAP[modeId];
    if (modeConf && TEMPLATE_LOCKED_MODES.includes(modeId)) {
      setSelectedTemplate(modeConf.defaultTemplate);
      templateRef.current = modeConf.defaultTemplate;

      // ── Restore saved state or use mode defaults ──
      const saved = getSavedModeState(modeId);
      if (saved) {
        // User previously customized this mode — restore their choices
        setSelectedBgPalette(saved.bgPalette);
        bgPaletteRef.current = saved.bgPalette;
        setSelectedGenerator(saved.generator);
        generatorRef.current = saved.generator;
        setSelectedTextPalette(saved.textPalette);
        textPaletteRef.current = saved.textPalette;
      } else {
        // First time entering this mode — use mode defaults
        setSelectedBgPalette(modeConf.defaultGenPalette);
        bgPaletteRef.current = modeConf.defaultGenPalette;
        setSelectedGenerator(modeConf.defaultGenerator);
        generatorRef.current = modeConf.defaultGenerator;

        const defaultText = DEFAULT_TEXT_PALETTE[modeConf.defaultTemplate] || DEFAULT_TEXT_PALETTE['normies-full-bg'];
        setSelectedTextPalette(defaultText);
        textPaletteRef.current = defaultText;
      }
    }

    // ── Normie Card / Agent Card: auto-select template on first entry ──
    if (modeConf && (modeId === 'normie-card' || modeId === 'agent-card')) {
      const saved = getSavedModeState(modeId);
      if (saved) {
        // Restore user's previous template + palette choices
        setSelectedTemplate(saved.template);
        templateRef.current = saved.template;
        setSelectedBgPalette(saved.bgPalette);
        bgPaletteRef.current = saved.bgPalette;
        setSelectedGenerator(saved.generator);
        generatorRef.current = saved.generator;
        setSelectedTextPalette(saved.textPalette);
        textPaletteRef.current = saved.textPalette;
      } else {
        // First time — auto-select Polaroid + mode defaults
        setSelectedTemplate(modeConf.defaultTemplate);
        templateRef.current = modeConf.defaultTemplate;
        setSelectedBgPalette(modeConf.defaultGenPalette);
        bgPaletteRef.current = modeConf.defaultGenPalette;
        setSelectedGenerator(modeConf.defaultGenerator);
        generatorRef.current = modeConf.defaultGenerator;

        const defaultText = DEFAULT_TEXT_PALETTE[modeConf.defaultTemplate] || DEFAULT_TEXT_PALETTE['normies-full-bg'];
        setSelectedTextPalette(defaultText);
        textPaletteRef.current = defaultText;
      }
    }

    // Fetch mode-specific extra data if not already available
    try {
      if ((modeId === 'market-dashboard') && !marketDataRef.current) {
        const [marketRes, historyStats] = await Promise.all([
          fetchMarketStats(),
          fetchHistoryStats(),
        ]);
        marketDataRef.current = {
          stats: marketRes?.stats || {},
          historyStats: historyStats || {},
        };
      } else if (modeId === 'burn-story' && !burnDataRef.current && dataRef.current) {
        const tid = dataRef.current.tokenId;
        const [burnHistory, burnedInfo] = await Promise.all([
          fetchBurnHistory(tid),
          fetchBurnedToken(tid),
        ]);
        burnDataRef.current = { burnHistory, burnedInfo };
      }
    } catch (fetchErr) {
      console.warn('[NormiesEditor] Mode data fetch failed:', fetchErr.message);
      setError(`Failed to load ${modeId} data — tap card again to retry`);
      // Continue — applyToSlide will use empty fallback data
    }

    if (dataRef.current) {
      applyToSlide(dataRef.current, modeId, templateRef.current, bgPaletteRef.current, generatorRef.current, textPaletteRef.current, polaroidColorRef.current, 'mode');
    } else if (!loadingRef.current) {
      // Data not loaded and no load in progress — trigger a fresh load.
      // All mode/template/palette refs have been updated above, so doLoad
      // will apply with the correct mode when it completes.
      doLoad(DEFAULT_NORMIES_TOKEN_ID);
    }
    // else: load is in progress — doLoad will apply with modeRef.current when it completes

    // Re-enable external sync after the render cycle settles
    setTimeout(() => { suppressExternalSyncRef.current = false; }, 100);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyToSlide, saveModeState, getSavedModeState, doLoad]);

  const handleTemplateChange = useCallback((templateId) => {
    // If user is still on the old template's default palette, switch to the
    // new template's default so each template starts with its own colours.
    const oldDefault = DEFAULT_TEXT_PALETTE[templateRef.current] || DEFAULT_TEXT_PALETTE['normies-full-bg'];
    const newDefault = DEFAULT_TEXT_PALETTE[templateId] || DEFAULT_TEXT_PALETTE['normies-full-bg'];
    let textPalette = textPaletteRef.current;
    if (textPalette === oldDefault && oldDefault !== newDefault) {
      textPalette = newDefault;
      setSelectedTextPalette(newDefault);
      textPaletteRef.current = newDefault;
    }
    setSelectedTemplate(templateId); templateRef.current = templateId;
    saveModeState(modeRef.current);
    if (dataRef.current) {
      applyToSlide(dataRef.current, modeRef.current, templateId, bgPaletteRef.current, generatorRef.current, textPalette, polaroidColorRef.current, 'template');
    } else {
      // No Normie loaded yet — still apply Normies gen art defaults + template
      onChange({
        template: templateId,
        generative: {
          type: generatorRef.current,
          paletteId: bgPaletteRef.current,
          enabled: true,
        },
        palette: textPalette,
        normies: { ...config, polaroidColor: polaroidColorRef.current },
      }, true);
    }
  }, [applyToSlide, onChange, config]);

  const handleBgPaletteChange = useCallback((paletteId) => {
    setSelectedBgPalette(paletteId); bgPaletteRef.current = paletteId;
    saveModeState(modeRef.current);
    if (dataRef.current) applyToSlide(dataRef.current, modeRef.current, templateRef.current, paletteId, generatorRef.current, textPaletteRef.current, polaroidColorRef.current, 'bgPalette');
  }, [applyToSlide, saveModeState]);

  const handleGeneratorChange = useCallback((genId) => {
    setSelectedGenerator(genId); generatorRef.current = genId;
    saveModeState(modeRef.current);
    if (dataRef.current) applyToSlide(dataRef.current, modeRef.current, templateRef.current, bgPaletteRef.current, genId, textPaletteRef.current, polaroidColorRef.current, 'generator');
  }, [applyToSlide, saveModeState]);

  const handleTextPaletteChange = useCallback((paletteId) => {
    setSelectedTextPalette(paletteId); textPaletteRef.current = paletteId;
    saveModeState(modeRef.current);
    if (dataRef.current) applyToSlide(dataRef.current, modeRef.current, templateRef.current, bgPaletteRef.current, generatorRef.current, paletteId, polaroidColorRef.current, 'textPalette');
  }, [applyToSlide, saveModeState]);

  const handlePolaroidColorChange = useCallback((hex) => {
    setSelectedPolaroidColor(hex); polaroidColorRef.current = hex;

    // Determine the recommended palette for this frame color
    const isDark = isColorDark(hex);
    const recommendedPalette = isDark ? 'mint' : 'normies';

    // Only auto-switch palette if the user hasn't manually chosen one.
    // "Manually chosen" = current palette differs from what we last auto-set.
    const currentPalette = textPaletteRef.current;
    const wasAutoSet = autoSetPaletteRef.current !== null && currentPalette === autoSetPaletteRef.current;
    const isDefault = currentPalette === DEFAULT_TEXT_PALETTE['normies-polaroid'];

    let textPal = currentPalette;
    if (wasAutoSet || isDefault) {
      textPal = recommendedPalette;
      setSelectedTextPalette(textPal); textPaletteRef.current = textPal;
      autoSetPaletteRef.current = textPal;
    }

    if (dataRef.current) applyToSlide(dataRef.current, modeRef.current, templateRef.current, bgPaletteRef.current, generatorRef.current, textPal, hex, 'polaroidColor');
  }, [applyToSlide]);

  // ── Status info ──
  const statusInfo = useMemo(() => {
    if (!loadedData) return null;
    const type = loadedData.traits?.attributes?.find(a => a.trait_type === 'Type')?.value || 'Unknown';
    const level = loadedData.canvasInfo?.level ?? '?';
    const name = loadedData.agentInfo?.name;
    return { type, level, name, tokenId: loadedData.tokenId, hasAgent: !!loadedData.agentBinding };
  }, [loadedData]);

  // Background art palettes — normies family + current external palette if any
  // Canvas Story: swap Burn → Ocean
  // Burn Story: swap Awakened → Inferno, Emerald → Ember
  const bgPalettes = useMemo(() => {
    // Filter out Gen Art-only palettes (Classic, Inverted, Phantom, Gilded)
    const normies = getPalettesByFamily('normies').filter(p => !p.genArtOnly);
    let list = normies;
    if (selectedMode === 'canvas-story') {
      const ocean = PALETTE_MAP['organic-ocean'];
      if (ocean) {
        list = normies.map(p => p.id === 'normies-burn' ? ocean : p);
      }
    } else if (selectedMode === 'burn-story') {
      const inferno = PALETTE_MAP['burn-inferno'];
      const ember = PALETTE_MAP['burn-ember'];
      list = normies.map(p => {
        if (p.id === 'normies-awakened' && inferno) return inferno;
        if (p.id === 'normies-emerald' && ember) return ember;
        return p;
      });
    } else if (selectedMode === 'gm') {
      // GM: use normies palettes + all GEO palettes
      const geoPalettes = getPalettesByFamily('geo');
      list = [...normies, ...geoPalettes];
    }
    // If the active palette isn't in the list, inject it so it's visible
    if (selectedBgPalette && !list.some(p => p.id === selectedBgPalette)) {
      const ext = PALETTE_MAP[selectedBgPalette];
      if (ext) return [...list, ext];
    }
    return list;
  }, [selectedBgPalette, selectedMode]);

  return (
    <div className="field-container">
      {/* ── Header + Toggle ── */}
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0">
          <div className="module-header">Normies Studio</div>
          <div className="module-desc">Generate posts from on-chain Normies data.</div>
        </div>
        <ToggleSwitch checked={enabled} onChange={handleToggle} />
      </div>

      <CollapseTransition open={enabled}>
        <div className="space-y-4">

          {/* ── Normie ID (genart-seed-lcd) — first field ── */}
          <div>
            <SectionHeader label="Normie ID" />
            <div className="flex items-center gap-2">
              <div className="genart-seed-lcd">
                <input type="number" value={tokenInput}
                  onChange={(e) => { setTokenInput(e.target.value); setError(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleLoad(); }}
                  className="genart-seed-input"
                  style={{ color: '#FF6600', textShadow: '0 0 8px rgba(255,102,0,0.3)' }}
                />
              </div>
              <button onClick={handleLoad} disabled={loading} className="btn-instrument btn-instrument-sm" title="Load Normie data">
                {loading ? <div className="w-3 h-3 rounded-full border-[2px] border-white/20 border-t-white/80 animate-spin" /> : <><Search size={12} strokeWidth={2.5} /> Load</>}
              </button>
            </div>
          </div>

          {error && (
            <div style={{
              fontFamily: 'IBM Plex Mono, monospace', fontSize: '10px', fontWeight: 500, letterSpacing: '0.3px',
              color: '#ef4444', textShadow: '0 0 6px rgba(239,68,68,0.2)',
              background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: '5px', padding: '6px 10px',
            }}>{error}</div>
          )}

          {/* ── LCD Progress Bar ── */}
          {loading && (
            <div className="genart-seed-lcd" style={{ padding: '8px 12px' }}>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-[#34d399] animate-spin flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '10px', fontWeight: 600, letterSpacing: '1px',
                    color: '#34d399', textShadow: '0 0 8px rgba(52,211,153,0.3)',
                  }}>
                    {LOAD_STEPS[loadStep]?.label || 'LOADING...'}
                  </div>
                  <div className="mt-1.5 h-[3px] rounded-full overflow-hidden" style={{ background: '#0a0c0a' }}>
                    <div className="h-full rounded-full transition-all duration-500 ease-out" style={{
                      width: `${((loadStep + 1) / LOAD_STEPS.length) * 100}%`,
                      background: 'linear-gradient(90deg, #34d399, #059669)',
                      boxShadow: '0 0 6px rgba(52,211,153,0.4)',
                    }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Loaded Normie Preview ── */}
          {statusInfo && !loading && (
            <div style={{
              background: '#0a0a0c', border: '1px solid #000', borderRadius: '6px', padding: '8px',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.8), 0 1px 1px rgba(255,255,255,0.04)',
            }}>
              <div className="flex items-center gap-3">
                <img
                  src={`https://api.normies.art/normie/${statusInfo.tokenId}/image.png`}
                  alt="" className="flex-shrink-0"
                  style={{ width: 44, height: 44, imageRendering: 'pixelated', borderRadius: '3px', border: '1px solid rgba(255,255,255,0.06)' }}
                />
                <div className="min-w-0 flex-1">
                  <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: '11px', fontWeight: 600, color: '#b0b2b5', letterSpacing: '0.3px' }}>
                    {statusInfo.name ? statusInfo.name : `NORMIE #${statusInfo.tokenId}`}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: '9px', fontWeight: 500, letterSpacing: '0.3px', color: '#6a6c6f', textTransform: 'uppercase' }}>
                      {statusInfo.type} · LVL {statusInfo.level}
                    </span>
                    {statusInfo.hasAgent && (
                      <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: '8px', fontWeight: 600, letterSpacing: '0.3px', color: '#6366f1', textShadow: '0 0 6px rgba(99,102,241,0.3)', textTransform: 'uppercase' }}>
                        · AWAKENED
                      </span>
                    )}
                  </div>
                </div>
                <div className="instrument-slider-badge instrument-slider-badge-value flex-shrink-0" style={{ width: '52px', height: '20px' }}>
                  <span className="instrument-slider-value" style={{ fontSize: '10px', color: '#FF6600', textShadow: '0 0 5px rgba(255,102,0,0.3)' }}>
                    #{statusInfo.tokenId}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ── Mode Selector (genart-family-btn) ── */}
          <div>
            <SectionHeader label="Mode" />
            <div className="genart-family-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              {NORMIES_MODES.map(mode => {
                const isActive = selectedMode === mode.id;
                const accent = mode.id === 'agent-card' ? '#6366f1'
                  : mode.id === 'canvas-story' ? '#34d399'
                  : mode.id === 'market-dashboard' ? '#f59e0b'
                  : mode.id === 'burn-story' ? '#ef4444'
                  : mode.id === 'gm' ? '#fbbf24'
                  : mode.id === 'explainer' ? '#60a5fa'
                  : '#48494b';
                const glow = mode.id === 'agent-card' ? 'rgba(99,102,241,0.3)'
                  : mode.id === 'canvas-story' ? 'rgba(52,211,153,0.3)'
                  : mode.id === 'market-dashboard' ? 'rgba(245,158,11,0.3)'
                  : mode.id === 'burn-story' ? 'rgba(239,68,68,0.3)'
                  : mode.id === 'gm' ? 'rgba(251,191,36,0.3)'
                  : mode.id === 'explainer' ? 'rgba(96,165,250,0.3)'
                  : 'rgba(72,73,75,0.3)';
                const ModeIcon = mode.id === 'agent-card' ? Bot
                  : mode.id === 'canvas-story' ? Palette
                  : mode.id === 'market-dashboard' ? BarChart3
                  : mode.id === 'burn-story' ? Flame
                  : mode.id === 'gm' ? Sun
                  : mode.id === 'explainer' ? BookOpen
                  : IdCard;
                // GM spans 2 columns (odd number of modes)
                const span2 = mode.id === 'gm';
                return (
                  <button key={mode.id} className={`genart-family-btn${isActive ? ' genart-family-btn-active' : ''}`}
                    style={span2 ? { gridColumn: 'span 2' } : undefined}
                    onClick={() => handleModeApply(mode.id)}>
                    <div className={`genart-family-led${isActive ? ' genart-family-led-active' : ''}`}
                      style={isActive ? { background: accent, '--fam-led-glow': glow } : undefined} />
                    <span className={`genart-family-label${isActive ? ' genart-family-label-active' : ''}`}
                      style={isActive ? { color: accent, '--fam-label-glow': glow } : undefined}>
                      <ModeIcon size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '4px' }} />
                      {mode.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Template Selector — only for modes with template choice ── */}
          {!TEMPLATE_LOCKED_MODES.includes(selectedMode) && (
          <div>
            <SectionHeader label="Template" />
            <div className="genart-family-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              {NORMIES_TEMPLATES.map(tpl => {
                const isActive = selectedTemplate === tpl.id;
                return (
                  <button key={tpl.id} className={`genart-family-btn${isActive ? ' genart-family-btn-active' : ''}`}
                    onClick={() => handleTemplateChange(tpl.id)}>
                    <div className={`genart-family-led${isActive ? ' genart-family-led-active' : ''}`}
                      style={isActive ? { background: '#e3e5e4', '--fam-led-glow': 'rgba(227,229,228,0.3)' } : undefined} />
                    <span className={`genart-family-label${isActive ? ' genart-family-label-active' : ''}`}
                      style={isActive ? { color: '#e3e5e4', '--fam-label-glow': 'rgba(227,229,228,0.3)' } : undefined}>
                      {tpl.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* ── Explainer Topic Picker — only visible in explainer mode ── */}
          {selectedMode === 'explainer' && (
            <div>
              <SectionHeader label="Topic" />
              <div className="genart-family-grid" style={{ gridTemplateColumns: '1fr' }}>
                {EXPLAINER_TOPICS.map(topic => {
                  const isActive = explainerTopicRef.current === topic.id;
                  return (
                    <button key={topic.id}
                      className={`genart-family-btn${isActive ? ' genart-family-btn-active' : ''}`}
                      onClick={() => {
                        explainerTopicRef.current = topic.id;
                        if (dataRef.current) {
                          applyToSlide(dataRef.current, modeRef.current, templateRef.current, bgPaletteRef.current, generatorRef.current, textPaletteRef.current, polaroidColorRef.current, 'topic');
                        } else if (!loadingRef.current) {
                          doLoad(DEFAULT_NORMIES_TOKEN_ID);
                        }
                      }}>
                      <div className={`genart-family-led${isActive ? ' genart-family-led-active' : ''}`}
                        style={isActive ? { background: '#60a5fa', '--fam-led-glow': 'rgba(96,165,250,0.3)' } : undefined} />
                      <span className={`genart-family-label${isActive ? ' genart-family-label-active' : ''}`}
                        style={isActive ? { color: '#60a5fa', '--fam-label-glow': 'rgba(96,165,250,0.3)' } : undefined}>
                        {topic.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Background Generators — hidden in Full BG (Normie image IS the background) ── */}
          {selectedTemplate !== 'normies-full-bg' && (<div>
            <SectionHeader label="Background" onMore={() => scrollEditorToSection('genart')} />
            <div className="normies-generator-grid">
              {activeGenerators.map(genId => {
                const gen = GENERATORS.find(g => g.id === genId);
                if (!gen) return null;
                const isActive = selectedGenerator === genId;
                return (
                  <button key={genId}
                    className={`genart-generator-btn${isActive ? ' genart-generator-btn-active' : ''}`}
                    onClick={() => handleGeneratorChange(genId)}
                    title={gen.name}
                  >
                    {/* LED dot */}
                    <div
                      className={`genart-generator-led${isActive ? ' genart-generator-led-active' : ''}`}
                      style={isActive ? {
                        background: NORMIES_GEN_ACCENT,
                        '--led-color': NORMIES_GEN_ACCENT,
                      } : undefined}
                    />
                    {/* Thumbnail */}
                    <div className="genart-generator-thumb">
                      {generatorThumbs[genId] ? (
                        <img src={generatorThumbs[genId]} alt="" draggable={false} className="genart-generator-thumb-img" />
                      ) : (
                        <div className="w-full h-full" style={{ background: '#0a0a0c' }} />
                      )}
                    </div>
                    {/* Label */}
                    <div className="genart-generator-label">{gen.name}</div>
                  </button>
                );
              })}
            </div>
          </div>)}

          {/* ── Polaroid Frame Color (only when polaroid template) ── */}
          {selectedTemplate === 'normies-polaroid' && (
            <div>
              <SectionHeader label="Frame Color" />
              <div className="normies-palette-grid">
                {POLAROID_COLORS.map(color => {
                  const isActive = selectedPolaroidColor === color.hex;
                  const isDarkColor = isColorDark(color.hex);
                  const accentHex = isDarkColor ? '#ffffff' : color.hex;
                  const rgb = hexToRgb(accentHex);
                  const ledGlow = rgb ? `rgba(${rgb.r},${rgb.g},${rgb.b},0.6)` : 'rgba(72,73,75,0.6)';
                  return (
                    <button key={color.id}
                      className={`genart-palette-btn${isActive ? ' genart-palette-btn-active' : ''}`}
                      onClick={() => handlePolaroidColorChange(color.hex)}
                      title={color.label}
                    >
                      <div
                        className={`genart-palette-led${isActive ? ' genart-palette-led-on' : ''}`}
                        style={isActive ? { background: accentHex, '--pal-led-glow': ledGlow } : undefined}
                      />
                      <div className="color-swatch-grid" style={isDarkColor ? { gap: '1px', backgroundColor: 'rgba(255,255,255,0.12)' } : undefined}>
                        {[0,1,2,3].map(i => (
                          <span key={i} className="color-swatch-cell" style={{ backgroundColor: color.hex }} />
                        ))}
                      </div>
                      <span className={`genart-palette-name${isActive ? ' genart-palette-name-active' : ''}`}
                        style={isActive ? { color: accentHex } : undefined}>
                        {color.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Art Palette — hidden in Full BG (no generative background to style) ── */}
          {selectedTemplate !== 'normies-full-bg' && (<div>
            <SectionHeader label="Art Palette" onMore={() => scrollEditorToSection('genart')} />
            <div className="normies-palette-grid">
              {bgPalettes.map(pal => {
                const isActive = selectedBgPalette === pal.id;
                const prepared = preparePalette(pal, 0, 1, 1);
                const swatchColors = prepared.colorsHex?.length ? prepared.colorsHex.slice(0, 4) : pal.colors.slice(0, 4);
                const accentHex = pal.accent || pal.colors[Math.floor(pal.colors.length / 2)] || pal.bg;
                const rgb = hexToRgb(accentHex);
                const ledGlow = rgb ? `rgba(${rgb.r},${rgb.g},${rgb.b},0.6)` : 'rgba(72,73,75,0.6)';
                return (
                  <button key={pal.id}
                    className={`genart-palette-btn${isActive ? ' genart-palette-btn-active' : ''}`}
                    onClick={() => handleBgPaletteChange(pal.id)}
                    title={pal.name}
                  >
                    <div
                      className={`genart-palette-led${isActive ? ' genart-palette-led-on' : ''}`}
                      style={isActive ? { background: accentHex, '--pal-led-glow': ledGlow } : undefined}
                    />
                    <div className="color-swatch-grid">
                      {swatchColors.map((c, i) => (
                        <span key={i} className="color-swatch-cell" style={{ backgroundColor: c }} />
                      ))}
                    </div>
                    <span className={`genart-palette-name${isActive ? ' genart-palette-name-active' : ''}`}
                      style={isActive ? { color: accentHex } : undefined}>
                      {pal.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>)}

          {/* ── Text Colors — same genart-palette-btn style, 4-col grid ── */}
          <div>
            <SectionHeader label="Text Colors" onMore={() => scrollEditorToSection('color')} />
            <div className="normies-palette-grid">
              {(selectedMode === 'gm' ? GM_TEXT_PALETTES : NORMIES_TEXT_PALETTES).map(preset => {
                const isActive = selectedTextPalette === preset.id;
                const swatchColors = textPaletteSwatchColors(preset);
                const accentHex = preset.colors.accent;
                const rgb = hexToRgb(accentHex);
                const ledGlow = rgb ? `rgba(${rgb.r},${rgb.g},${rgb.b},0.6)` : 'rgba(72,73,75,0.6)';
                return (
                  <button key={preset.id}
                    className={`genart-palette-btn${isActive ? ' genart-palette-btn-active' : ''}`}
                    onClick={() => handleTextPaletteChange(preset.id)}
                    title={preset.description}
                  >
                    <div
                      className={`genart-palette-led${isActive ? ' genart-palette-led-on' : ''}`}
                      style={isActive ? { background: accentHex, '--pal-led-glow': ledGlow } : undefined}
                    />
                    <div className="color-swatch-grid">
                      {swatchColors.map((c, i) => (
                        <span key={i} className="color-swatch-cell" style={{ backgroundColor: c }} />
                      ))}
                    </div>
                    <span className={`genart-palette-name${isActive ? ' genart-palette-name-active' : ''}`}
                      style={isActive ? { color: accentHex } : undefined}>
                      {preset.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

        </div>
      </CollapseTransition>
    </div>
  );
}
